// apps/admin/src/pages/auth/ForgotPasswordPage.tsx
//
// Phase 1 Task 47 — Forgot password screen (admin portal).
//
// Per CONTEXT + SPECS / AC-07:
//   - Email input → POST /api/v1/auth/forgot-password
//   - ALWAYS show generic success message regardless of API outcome
//     (defends against email-enumeration).
//   - Recaptcha token is enforced server-side (route middleware); FE sends an
//     empty body field so the body parser does not 400.
//
// Implementation notes
//   - react-hook-form + zod resolver — keeps inline validation consistent with
//     the other auth screens in this wave (Login/Register/Reset).
//   - apiClient() from @printway/shared/api — typed openapi-fetch wrapper.
//     Cast pattern matches `packages/ui/src/hooks/useAuth.ts` so we don't
//     wedge against the still-stub `paths` types.

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import {
  AuthSplitLayout,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

// Phase 8.x debug B1 — pre-auth locale switcher (anonymous reload).
import { AdminLanguageSelector } from '../../components/shell/AdminLanguageSelector.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Schema — mirrors apps/api `PostAuthForgotPasswordRequest`.
// ---------------------------------------------------------------------------

type Translate = (key: string, vars?: Record<string, unknown>) => string;

function makeForgotPasswordSchema(t: Translate) {
  return z.object({
    email: z.string().email(t('auth.forgot.validation.emailInvalid')).max(255),
  });
}
interface ForgotPasswordInput {
  email: string;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation('ops');
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string>('');

  const ForgotPasswordSchema = useMemo(() => makeForgotPasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(ForgotPasswordSchema as never),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordInput): Promise<void> => {
    // Email-enumeration defence: ignore network errors entirely so
    // the UI never leaks miss/hit signal. The handler always returns 200 on
    // happy + miss paths; only outright network failure could throw here.
    try {
      const client = apiClient();
      await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{ response: Response }>;
        }
      ).POST('/api/v1/auth/forgot-password', {
        body: { email: data.email, recaptchaToken: '' },
      });
    } catch {
      // Swallow — generic success message regardless.
    } finally {
      setSubmittedEmail(data.email);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <AuthSplitLayout variant="marketing">
      <div className="flex justify-end">
        <AdminLanguageSelector />
      </div>
      <div className="flex flex-1 items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('auth.forgot.success.title')}</CardTitle>
            <CardDescription>
              {t('auth.forgot.success.descBefore')}{' '}
              <span className="font-medium text-pw-text">{submittedEmail}</span>{' '}
              {t('auth.forgot.success.descAfter')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                {t('auth.forgot.backToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout variant="marketing">
      <div className="flex justify-end">
        <AdminLanguageSelector />
      </div>
      <div className="flex flex-1 items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('auth.forgot.title')}</CardTitle>
          <CardDescription>
            {t('auth.forgot.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormField>
              <FormLabel htmlFor="email">{t('auth.forgot.emailLabel')}</FormLabel>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t('auth.forgot.emailPlaceholder')}
                {...register('email')}
              />
              {errors.email ? (
                <FormMessage>{errors.email.message}</FormMessage>
              ) : null}
            </FormField>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
            </Button>

            <p className="text-center text-sm">
              <Link to="/login" className="text-pw-text-muted underline">
                {t('auth.forgot.backToLogin')}
              </Link>
            </p>
          </Form>
        </CardContent>
      </Card>
    </div>
    </AuthSplitLayout>
  );
}

export default ForgotPasswordPage;
